import { useEffect, useRef, useState } from "react";
import type {
  BuyerDealStructureDto,
  DealStructureCashFlowType,
  DealStructurePriceBasis,
  UpdateBuyerDealStructurePayload,
} from "@/api/buyers";
import { Modal } from "@/components/ui/Modal";
import { Button, useToast } from "@/design-system/components/ui";
import { usePatchBuyerDealStructure } from "@/hooks/buyers/useBuyerDealStructure";
import {
  EDIT_MODAL,
  SAVE_BUTTON,
  W1,
  W2,
  W3,
  W5,
  moneyText,
  parseMoneyText,
} from "./dealStructureCopy";
import {
  CashFlowFields,
  DownPaymentField,
  NoteField,
  PriceBasisCards,
  cashFlowReady,
  priceReady,
} from "./DealStructureFields";

/**
 * EDIT DEAL STRUCTURE — every field of the wizard in one form, saved at once.
 *
 * <p>The save sends every field plus `confirm: true`, so it is a complete save
 * like W5's: the server re-computes the figures and refuses (400, with the
 * sentence shown inline) anything the wizard would refuse. The notice above
 * the button says what saving confirms.</p>
 */
export function DealStructureEditModal({
  submissionId,
  dto,
  open,
  onClose,
}: {
  submissionId: string;
  dto: BuyerDealStructureDto;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const patch = usePatchBuyerDealStructure(submissionId);

  const [type, setType] = useState<DealStructureCashFlowType | null>(null);
  const [amountText, setAmountText] = useState("");
  const [estimateText, setEstimateText] = useState("");
  const [downText, setDownText] = useState("");
  const [basis, setBasis] = useState<DealStructurePriceBasis | null>(null);
  const [customText, setCustomText] = useState("");
  const [noteText, setNoteText] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Seeded once per open — the save's invalidation hands back a new `dto`.
  const seeded = useRef(false);
  useEffect(() => {
    if (!open) {
      seeded.current = false;
      return;
    }
    if (seeded.current) return;
    seeded.current = true;
    setType(dto.cashFlowType ?? null);
    setAmountText(moneyText(dto.cashFlow));
    setEstimateText(moneyText(dto.freeCashFlowEstimate));
    setDownText(moneyText(dto.downPayment));
    setBasis(dto.priceBasis ?? null);
    setCustomText(moneyText(dto.customPrice));
    setNoteText(dto.note ?? "");
    setError(null);
  }, [open, dto]);

  const amount = parseMoneyText(amountText);
  const estimate = parseMoneyText(estimateText);
  const down = parseMoneyText(downText, true);
  const ready = cashFlowReady(type, amountText, estimateText) && down != null && priceReady(basis, customText);

  // The saved supported price describes the SAVED cash flow and down payment;
  // once either is edited here it no longer does.
  const supportedStale =
    type !== (dto.cashFlowType ?? null)
    || amount !== (dto.cashFlow ?? null)
    || (type === "Sde" && estimate !== (dto.freeCashFlowEstimate ?? null))
    || down !== (dto.downPayment ?? null);

  async function submit() {
    if (!ready || !type || amount == null || down == null || !basis) return;
    const payload: UpdateBuyerDealStructurePayload = {
      cashFlowType: type,
      cashFlow: amount,
      ...(type === "Sde" && estimate != null ? { freeCashFlowEstimate: estimate } : {}),
      downPayment: down,
      priceBasis: basis,
      ...(basis === "Custom" ? { customPrice: parseMoneyText(customText) ?? undefined } : {}),
      note: noteText.trim(),
      confirm: true,
    };
    setError(null);
    try {
      await patch.mutateAsync(payload);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save the deal structure.");
      return;
    }
    toast.success(W5.saved);
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      size="lg"
      title={EDIT_MODAL.title}
      footer={(
        <div className="flex flex-wrap items-center justify-end w-full gap-space-2">
          <Button variant="secondary" disabled={patch.isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            data-testid="deal-structure-edit-save"
            disabled={patch.isPending || !ready}
            onClick={() => void submit()}
          >
            {SAVE_BUTTON}
          </Button>
        </div>
      )}
    >
      <div className="grid gap-space-5">
        <section className="grid gap-space-2">
          <h3 className="text-small font-semibold text-onyx m-0">{W1.question}</h3>
          <CashFlowFields
            idPrefix="deal-structure-edit"
            type={type}
            onType={setType}
            amountText={amountText}
            onAmountText={setAmountText}
            estimateText={estimateText}
            onEstimateText={setEstimateText}
          />
        </section>
        <section className="grid gap-space-2">
          <h3 className="text-small font-semibold text-onyx m-0">{W2.question}</h3>
          <DownPaymentField idPrefix="deal-structure-edit" text={downText} onText={setDownText} />
        </section>
        <section className="grid gap-space-2">
          <h3 className="text-small font-semibold text-onyx m-0">{W3.question}</h3>
          <PriceBasisCards
            idPrefix="deal-structure-edit"
            basis={basis}
            onBasis={setBasis}
            customText={customText}
            onCustomText={setCustomText}
            figures={dto.figures}
            supportedStale={supportedStale}
          />
        </section>
        <NoteField idPrefix="deal-structure-edit" text={noteText} onText={setNoteText} />
        <p className="text-small text-gray-mid m-0" data-testid="deal-structure-edit-notice">
          {EDIT_MODAL.notice}
        </p>
        {error && (
          <p role="alert" className="text-small text-status-error m-0" data-testid="deal-structure-edit-error">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
